import React, { useState } from 'react';
import {
  Download,
  ArrowLeft,
  ArrowRight,
  Loader,
  CheckCircle,
  AlertCircle,
  BarChart3,
  Eye,
  EyeOff,
  Mail,
  Plus,
  X,
} from 'lucide-react';
import { TestCaseComparisonViewer } from './TestCaseComparisonViewer';
import TestingTypeModal from '../modals/TestingTypeModal';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { validateEmail } from '../../utils/validators';
import { buildTestCaseComparisonMetrics } from '../../utils/testCaseComparison';
import { announceJob, submitJobOperation, waitForJobResult } from '../../services/jobs';

const RECIPIENTS_STORAGE_KEY = 'testcase_email_recipients';
const LEGACY_RECIPIENT_STORAGE_KEY = 'testcase_email_recipient';

const parseRecipientEmails = (value) => (
  [...new Set(
    String(value || '')
      .split(/[\n,;]+/)
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
  )]
);

const normalizeRecipientInputs = (inputs) => {
  const normalized = [];
  const seen = new Set();
  (Array.isArray(inputs) ? inputs : []).forEach((entry) => {
    const candidate = String(entry || '').trim().toLowerCase();
    if (!candidate || seen.has(candidate)) return;
    seen.add(candidate);
    normalized.push(candidate);
  });
  return normalized;
};

const getInitialRecipientInputs = (currentUserEmail = '') => {
  const savedRecipients = localStorage.getItem(RECIPIENTS_STORAGE_KEY);
  if (savedRecipients) {
    try {
      const parsed = JSON.parse(savedRecipients);
      if (Array.isArray(parsed) && parsed.length > 0) {
        const normalized = normalizeRecipientInputs(parsed);
        if (normalized.length > 0) return normalized;
      }
    } catch (_err) {
      const normalized = parseRecipientEmails(savedRecipients);
      if (normalized.length > 0) return normalized;
    }
  }

  const legacyRecipient = String(localStorage.getItem(LEGACY_RECIPIENT_STORAGE_KEY) || '').trim();
  if (legacyRecipient) {
    const normalizedLegacy = parseRecipientEmails(legacyRecipient);
    if (normalizedLegacy.length > 0) return normalizedLegacy;
  }

  const normalizedUserEmail = String(currentUserEmail || '').trim().toLowerCase();
  if (validateEmail(normalizedUserEmail)) {
    return [normalizedUserEmail];
  }
  return [''];
};

export const SaveTestsStep = ({
  testCases,
  baselineTestCases = null,
  pdfId,
  currentUserEmail = '',
  uploadedPdfName,
  apiResponse,
  processingResult,
  testCaseApprovalStatus = null,
  testCaseApprovalLoading = false,
  canRequestTestCaseApproval = false,
  canApproveTestCaseApproval = false,
  canProceedToPOM = true,
  isApiScriptFlow = false,
  onRequestTestCaseApproval = null,
  onApproveTestCaseApproval = null,
  onRejectTestCaseApproval = null,
  onBack,
  onGeneratePOM,
  testingTypeMode = 'select',
  showApprovalWorkflow = true,
  sourceLabel = 'BRD',
  pomActionLabel = 'Generate POM',
  showBackButton = true,
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState(null);
  const [downloadError, setError] = useState(null);
  const [recipientInputs, setRecipientInputs] = useState(() => getInitialRecipientInputs(currentUserEmail));
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [lastSentRecipients, setLastSentRecipients] = useState([]);
  const [showComparison, setShowComparison] = useState(false);
  const [approvalActionError, setApprovalActionError] = useState(null);
  const [approvalActionStatus, setApprovalActionStatus] = useState(null);
  const [showTestingTypeModal, setShowTestingTypeModal] = useState(false);
  const resolvedFilename = apiResponse?.filename || testCases?.filename || 'TestCases.xlsx';
  const sectionNumber = Number(processingResult?.section_number || 0);
  const hasSectionNumber = Number.isFinite(sectionNumber) && sectionNumber > 0;
  const testCaseApprovalState = String(testCaseApprovalStatus?.approval_state || 'draft').toLowerCase();
  const testCaseApprovalStateLabel = {
    draft: 'Draft',
    pending_review: 'Pending Review',
    approved: 'Approved',
    changes_requested: 'Changes Requested',
  }[testCaseApprovalState] || 'Draft';
  const testCaseApprovalStateClass = {
    draft: 'border-slate-200 bg-slate-100 text-slate-700',
    pending_review: 'border-teal-200 bg-teal-50 text-teal-800',
    approved: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    changes_requested: 'border-rose-200 bg-rose-50 text-rose-800',
  }[testCaseApprovalState] || 'border-slate-200 bg-slate-50 text-slate-800';
  const canRequesterActOnTestCaseApproval = showApprovalWorkflow && canRequestTestCaseApproval && !canApproveTestCaseApproval;
  const canShowTestCaseApprovalRequest = canRequesterActOnTestCaseApproval && testCaseApprovalState !== 'pending_review' && !canProceedToPOM;
  const canShowTestCaseApprovalActions = showApprovalWorkflow && canApproveTestCaseApproval && !canProceedToPOM;
  const requesterOutputLocked = showApprovalWorkflow && canRequesterActOnTestCaseApproval && !canProceedToPOM;

  const handleDownloadTestCases = async () => {
    setIsDownloading(true);
    setDownloadStatus(null);
    setError(null);

    try {
      if (apiResponse?.job?.id) {
        const queuedJob = announceJob(apiResponse.job);
        await waitForJobResult(queuedJob);
        window.open(`${API_BASE_URL}${API_ENDPOINTS.JOB_DOWNLOAD(queuedJob.id)}`, '_blank', 'noopener,noreferrer');
        setDownloadStatus('completed');
        setIsDownloading(false);
        return;
      }
      const encodedFilename = encodeURIComponent(resolvedFilename);
      const sectionQuery = hasSectionNumber
        ? `&section_number=${encodeURIComponent(sectionNumber)}`
        : '';
      const downloadUrl = `${API_BASE_URL}${API_ENDPOINTS.DOWNLOAD_RESULTS}/${pdfId}/${encodedFilename}?t=${Date.now()}${sectionQuery}`;

      window.open(downloadUrl, '_blank', 'noopener,noreferrer');

      setDownloadStatus('completed');
      setTimeout(() => setIsDownloading(false), 500);
    } catch (err) {
      setError('Failed to download test cases: ' + err.message);
      setIsDownloading(false);
    }
  };

  const handleSendExcelEmail = async () => {
    const enteredRecipients = normalizeRecipientInputs(recipientInputs);
    const invalidRecipients = enteredRecipients.filter((recipient) => !validateEmail(recipient));
    const recipients = enteredRecipients.filter((recipient) => validateEmail(recipient));

    if (recipients.length === 0) {
      setEmailStatus(null);
      setEmailError('Please enter at least one recipient email address.');
      return;
    }

    if (invalidRecipients.length > 0) {
      setEmailStatus(null);
      setEmailError(
        `Invalid recipient email${invalidRecipients.length > 1 ? 's' : ''}: ${invalidRecipients.join(', ')}`
      );
      return;
    }

    if (!resolvedFilename) {
      setEmailStatus(null);
      setEmailError('Excel filename is missing. Please save test cases again.');
      return;
    }

    setIsSendingEmail(true);
    setEmailStatus(null);
    setEmailError(null);

    try {
      if (apiResponse?.job?.id) {
        await waitForJobResult(announceJob(apiResponse.job));
      }
      const endpoint = `${API_BASE_URL}${API_ENDPOINTS.EMAIL_TEST_CASES_RESULTS}/${pdfId}`;
      const { job } = await submitJobOperation(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: recipients[0],
          emails: recipients,
          filename: resolvedFilename,
          section_title: processingResult?.section_title || null,
          ...(hasSectionNumber ? { section_number: sectionNumber } : {}),
          ...(apiResponse?.job?.id ? { export_job_id: apiResponse.job.id } : {}),
        }),
      }, { errorMessage: 'Unable to queue email delivery.' });
      setEmailStatus('queued');
      await waitForJobResult(job);

      localStorage.setItem(RECIPIENTS_STORAGE_KEY, JSON.stringify(recipients));
      localStorage.setItem(LEGACY_RECIPIENT_STORAGE_KEY, recipients[0] || '');
      setRecipientInputs(recipients);
      setLastSentRecipients(recipients);
      setEmailStatus('completed');
    } catch (err) {
      setEmailStatus(null);
      setEmailError(err.message || 'Failed to send email');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const formatLeadNotificationStatus = (responsePayload, baseMessage) => {
    const targeted = Number(
      responsePayload?.notification?.application_leads_targeted
      || responsePayload?.notification?.application_reviewers_targeted
      || responsePayload?.notification?.workspace_leads_targeted
      || responsePayload?.notification?.workspace_reviewers_targeted
      || 0
    );
    if (Number.isFinite(targeted) && targeted > 0) {
      return `${baseMessage} Notified ${targeted} application lead${targeted === 1 ? '' : 's'}.`;
    }
    return baseMessage;
  };

  const handleRequestApproval = async () => {
    if (!onRequestTestCaseApproval || testCaseApprovalLoading) return;
    setApprovalActionError(null);
    setApprovalActionStatus(null);
    try {
      const responsePayload = await onRequestTestCaseApproval();
      setApprovalActionStatus(
        formatLeadNotificationStatus(responsePayload, 'Approval request sent.')
      );
    } catch (err) {
      setApprovalActionError(err?.message || 'Failed to request approval.');
    }
  };

  const handleApprove = async () => {
    if (!onApproveTestCaseApproval || testCaseApprovalLoading) return;
    setApprovalActionError(null);
    setApprovalActionStatus(null);
    try {
      await onApproveTestCaseApproval();
      setApprovalActionStatus('Test cases approved.');
    } catch (err) {
      setApprovalActionError(err?.message || 'Failed to approve test cases.');
    }
  };

  const handleReject = async () => {
    if (!onRejectTestCaseApproval || testCaseApprovalLoading) return;
    setApprovalActionError(null);
    setApprovalActionStatus(null);
    try {
      await onRejectTestCaseApproval();
      setApprovalActionStatus('Changes requested from QA/Developer.');
    } catch (err) {
      setApprovalActionError(err?.message || 'Failed to reject test cases.');
    }
  };

  const localComparisonBundle = buildTestCaseComparisonMetrics(testCases, baselineTestCases);
  const comparisonBundle = localComparisonBundle || apiResponse?.metrics || {};
  const metrics = comparisonBundle.overall || comparisonBundle;
  const comparisonMethod = metrics.comparison_method || comparisonBundle.comparison_method || {
    label: 'Ordered text match',
    description: 'Compares wording and sequence so step order changes lower the score.',
  };

  const hasComparisonMetrics =
    comparisonBundle?.comparison_scope === 'initial_ai_baseline' ||
    typeof metrics.overall_average_similarity === 'number';
  const testCasesWithChanges = metrics.statistics?.test_cases_with_changes || 0;
  const comparisons = Array.isArray(metrics.comparisons) ? metrics.comparisons : [];
  const changedComparisonsCount = comparisons.filter((comparison) => comparison?.has_changes).length;
  const buildCategoryMetrics = (categoryKey) => {
    const categoryRows = comparisons.filter(
      (comparison) => String(comparison?.category || '').toLowerCase() === categoryKey
    );
    const compared = categoryRows.length;
    const changed = categoryRows.filter((comparison) => Boolean(comparison?.has_changes)).length;
    const unchanged = Math.max(0, compared - changed);
    const changePercent = compared > 0 ? (changed / compared) * 100 : 0;
    return {
      compared,
      changed,
      unchanged,
      changePercent: Number(changePercent.toFixed(2)),
    };
  };
  const positiveMetrics = buildCategoryMetrics('positive');
  const negativeMetrics = buildCategoryMetrics('negative');
  const formatChangeDetail = (changed, compared, label) => (
    compared > 0
      ? `Made changes in ${changed} out of ${compared} ${label}.`
      : `No ${label} found in baseline.`
  );
  const buildMetricTone = (changed, compared) => {
    if (compared === 0) {
      return {
        card: 'border-slate-200 bg-slate-50/70',
        badge: 'border-slate-200 bg-white text-slate-600',
      };
    }
    if (changed > 0) {
      return {
        card: 'border-amber-200/80 bg-amber-50/60',
        badge: 'border-amber-200 bg-white text-amber-800',
      };
    }
    return {
      card: 'border-emerald-200/80 bg-emerald-50/60',
      badge: 'border-emerald-200 bg-white text-emerald-800',
    };
  };
  const overallMetricTone = buildMetricTone(testCasesWithChanges, comparisons.length);
  const positiveMetricTone = buildMetricTone(positiveMetrics.changed, positiveMetrics.compared);
  const negativeMetricTone = buildMetricTone(negativeMetrics.changed, negativeMetrics.compared);
  const handleRecipientInputChange = (index, value) => {
    setRecipientInputs((previous) => previous.map((entry, itemIndex) => (
      itemIndex === index ? value : entry
    )));
    if (emailError) setEmailError(null);
    if (emailStatus) setEmailStatus(null);
  };
  const handleAddRecipientInput = () => {
    setRecipientInputs((previous) => [...previous, '']);
    if (emailError) setEmailError(null);
    if (emailStatus) setEmailStatus(null);
  };
  const handleRemoveRecipientInput = (index) => {
    setRecipientInputs((previous) => {
      const next = previous.filter((_, itemIndex) => itemIndex !== index);
      return next.length > 0 ? next : [''];
    });
    if (emailError) setEmailError(null);
    if (emailStatus) setEmailStatus(null);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="soft-card workspace-accent-soft-panel border-[color:var(--workspace-accent-border)] p-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <span className="workspace-context-icon inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-white">
              <CheckCircle className="h-5 w-5" />
            </span>
            <div>
              <p className="application-eyebrow text-[color:var(--workspace-accent-text)]">Current stage</p>
              <h3 className="mt-1 text-xl font-semibold text-slate-900">
                {showApprovalWorkflow ? 'Review & Approval Hub' : 'Review & Export'}
              </h3>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {showApprovalWorkflow
                  ? `Review, share, and approve test cases before moving to ${isApiScriptFlow ? 'API script generation.' : 'POM generation.'}`
                  : `Review and export test cases generated from this ${sourceLabel} before continuing to ${isApiScriptFlow ? 'API script generation' : 'POM'}.`}
              </p>
            </div>
          </div>
          {processingResult?.section_title && (
            <div className="flex lg:justify-end">
              <span className="workspace-accent-chip inline-flex max-w-full items-center rounded-full border px-3 py-1.5 text-xs font-semibold">
                Module: {processingResult.section_title}
              </span>
            </div>
          )}
        </div>
      </section>

      {showApprovalWorkflow && (
        <section className="soft-card space-y-4">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <p className="application-eyebrow">Test case approval</p>
                <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold ${testCaseApprovalStateClass}`}>
                  {testCaseApprovalStateLabel}
                </span>
              </div>
              <div>
                <h3 className="font-semibold text-slate-900">
                  {canProceedToPOM ? 'Approved to continue' : 'Approval required before automation'}
                </h3>
                <p className="mt-1 text-sm leading-6 text-slate-600">
                  {!canProceedToPOM
                    ? `Lead approval is required before moving to ${isApiScriptFlow ? 'API script generation' : 'POM generation and automation'}.`
                    : `This test case set is ready for ${isApiScriptFlow ? 'API script generation' : 'POM generation and automation'}.`}
                </p>
                {testCaseApprovalState === 'changes_requested' && testCaseApprovalStatus?.rejection_reason && (
                  <p className="mt-2 text-sm text-rose-700">
                    Reason: {testCaseApprovalStatus.rejection_reason}
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 lg:justify-end">
              {canShowTestCaseApprovalRequest && (
                <button
                  type="button"
                  className="secondary-btn workspace-accent-secondary px-3 py-2 text-sm"
                  onClick={handleRequestApproval}
                  disabled={testCaseApprovalLoading}
                >
                  {testCaseApprovalLoading ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <AlertCircle className="h-4 w-4" />
                  )}
                  {testCaseApprovalState === 'changes_requested' ? 'Send for Re-approval' : 'Send for Approval'}
                </button>
              )}
              {canShowTestCaseApprovalActions && (
                <>
                  <button
                    type="button"
                    className="primary-btn workspace-accent-button px-3 py-2 text-sm"
                    onClick={handleApprove}
                    disabled={testCaseApprovalLoading}
                  >
                    {testCaseApprovalLoading ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <CheckCircle className="h-4 w-4" />
                    )}
                    Approve
                  </button>
                  <button
                    type="button"
                    className="secondary-btn px-3 py-2 text-sm"
                    onClick={handleReject}
                    disabled={testCaseApprovalLoading}
                  >
                    Reject
                  </button>
                </>
              )}
            </div>
          </div>

          {approvalActionError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {approvalActionError}
            </div>
          )}
          {approvalActionStatus && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
              {approvalActionStatus}
            </div>
          )}
        </section>
      )}

      {/* Comparison Metrics Section */}
      {hasComparisonMetrics && (
        <section className="soft-card space-y-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-start gap-3">
              <div className="workspace-accent-soft-panel mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[color:var(--workspace-accent-border)]">
                <BarChart3 className="h-4 w-4 text-[color:var(--workspace-accent-text)]" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-slate-900">Human Edits vs Initial AI</h4>
                <p className="mt-1 text-sm text-slate-500">
                  Only manual edits are counted against the original AI baseline.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 sm:justify-end">
              <button
                onClick={() => setShowComparison(!showComparison)}
                className="secondary-btn workspace-accent-secondary px-3 py-2 text-sm"
              >
                {showComparison ? (
                  <>
                    <EyeOff className="h-4 w-4" />
                    Hide Changed Cases
                  </>
                ) : (
                  <>
                    <Eye className="h-4 w-4" />
                    View Changed Cases
                  </>
                )}
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className={`rounded-2xl border p-4 ${overallMetricTone.card}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Changed Test Cases</p>
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${overallMetricTone.badge}`}>
                  {testCasesWithChanges > 0 ? 'Updated' : comparisons.length > 0 ? 'Unchanged' : 'No baseline'}
                </span>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {testCasesWithChanges}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {formatChangeDetail(testCasesWithChanges, comparisons.length, 'cases')}
              </p>
            </div>

            <div className={`rounded-2xl border p-4 ${positiveMetricTone.card}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Positive Cases Changed</p>
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${positiveMetricTone.badge}`}>
                  {positiveMetrics.changed > 0 ? 'Updated' : positiveMetrics.compared > 0 ? 'Unchanged' : 'No baseline'}
                </span>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {positiveMetrics.changed}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {formatChangeDetail(positiveMetrics.changed, positiveMetrics.compared, 'positive cases')}
              </p>
            </div>

            <div className={`rounded-2xl border p-4 ${negativeMetricTone.card}`}>
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm font-semibold text-slate-900">Negative Cases Changed</p>
                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-semibold ${negativeMetricTone.badge}`}>
                  {negativeMetrics.changed > 0 ? 'Updated' : negativeMetrics.compared > 0 ? 'Unchanged' : 'No baseline'}
                </span>
              </div>
              <p className="mt-3 text-3xl font-bold text-slate-900">
                {negativeMetrics.changed}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                {formatChangeDetail(negativeMetrics.changed, negativeMetrics.compared, 'negative cases')}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Test Case Comparison Details */}
      {showComparison && hasComparisonMetrics && (
        <div className="soft-card">
          <TestCaseComparisonViewer
            comparisons={comparisons}
            comparisonMethod={comparisonMethod}
            changedCount={changedComparisonsCount}
          />
        </div>
      )}

      {/* Status Messages */}
      {downloadError && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
            <div>
              <p className="text-sm font-medium text-red-800">Error</p>
              <p className="mt-1 text-sm text-red-600">{downloadError}</p>
            </div>
          </div>
        </div>
      )}

      {downloadStatus === 'completed' && (
        <div className="workspace-accent-soft-panel rounded-xl border border-[color:var(--workspace-accent-border)] p-4">
          <div className="flex gap-3">
            <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-[color:var(--workspace-accent)]" />
            <div>
              <p className="text-sm font-medium text-slate-900">Workbook downloaded</p>
              <p className="mt-1 text-sm text-slate-600">Your test cases Excel file is ready to use.</p>
            </div>
          </div>
        </div>
      )}

      <section className={`soft-card space-y-4 ${requesterOutputLocked ? 'opacity-75' : ''}`}>
        <div className="flex items-start gap-3">
          <div className="workspace-accent-soft-panel flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[color:var(--workspace-accent-border)]">
            <Mail className="h-4 w-4 text-[color:var(--workspace-accent-text)]" />
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-900">Share workbook by email</p>
            <p className="mt-1 text-sm leading-6 text-slate-500">
              {requesterOutputLocked ? (
                'Email export is enabled after Lead approval.'
              ) : (
                <>
                  Send <span className="font-medium text-slate-700">{resolvedFilename}</span> to one or more email addresses.
                </>
              )}
            </p>
          </div>
        </div>

        <div className="space-y-2">
          {recipientInputs.map((recipient, index) => (
            <div key={`recipient-${index}`} className="flex gap-2">
              <input
                type="email"
                value={recipient}
                onChange={(event) => handleRecipientInputChange(index, event.target.value)}
                placeholder={`Recipient email ${index + 1}`}
                className="text-input"
                disabled={requesterOutputLocked || isSendingEmail}
              />
              <button
                type="button"
                onClick={() => handleRemoveRecipientInput(index)}
                disabled={requesterOutputLocked || isSendingEmail || recipientInputs.length === 1}
                className="secondary-btn h-11 w-11 shrink-0 px-0 py-0 text-slate-500 disabled:cursor-not-allowed disabled:opacity-40"
                title="Remove recipient"
                aria-label={`Remove recipient ${index + 1}`}
              >
                <X className="mx-auto h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={handleAddRecipientInput}
            disabled={requesterOutputLocked || isSendingEmail}
            className="secondary-btn h-11 w-full justify-center px-3 sm:w-auto sm:min-w-[160px] disabled:cursor-not-allowed disabled:opacity-70"
          >
            <Plus className="h-4 w-4" />
            Add recipient
          </button>
          <button
            onClick={handleSendExcelEmail}
            disabled={requesterOutputLocked || isSendingEmail}
            className="primary-btn workspace-accent-button h-11 w-full justify-center px-4 sm:w-auto sm:min-w-[180px] disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isSendingEmail ? (
              <>
                <Loader className="h-4 w-4 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send Excel
              </>
            )}
          </button>
        </div>

        {emailError && (
          <p className="text-sm text-red-600">{emailError}</p>
        )}
        {emailStatus === 'completed' && (
          <p className="text-sm text-emerald-700">
            Excel file emailed successfully to {lastSentRecipients.length} recipient{lastSentRecipients.length === 1 ? '' : 's'}.
          </p>
        )}
        {emailStatus === 'queued' && (
          <p className="text-sm text-teal-700">Email delivery is running in the background. You can follow or retry it in Background jobs.</p>
        )}
      </section>

      {/* Action Buttons */}
      <div className={`grid gap-3 border-t border-slate-200 pt-6 ${showBackButton && onBack ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
        {showBackButton && onBack && (
          <button
            onClick={onBack}
            disabled={isDownloading}
            className="secondary-btn h-12 w-full justify-center"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
        )}

        <button
          onClick={() => {
            if (isApiScriptFlow) {
              if (onGeneratePOM) onGeneratePOM('api');
              return;
            }
            if (testingTypeMode === 'web-only') {
              if (onGeneratePOM) onGeneratePOM('web');
              return;
            }
            setShowTestingTypeModal(true);
          }}
          disabled={!canProceedToPOM}
          className="primary-btn workspace-accent-button h-12 w-full justify-center px-6 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <ArrowRight className="h-5 w-5" />
          <span>{isApiScriptFlow ? 'Generate API Scripts' : pomActionLabel}</span>
        </button>

        <button
          onClick={handleDownloadTestCases}
          disabled={requesterOutputLocked || isDownloading || downloadStatus === 'completed'}
          className={`secondary-btn h-12 w-full justify-center px-6 ${
            downloadStatus === 'completed'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
              : 'workspace-accent-secondary'
          } disabled:cursor-not-allowed disabled:opacity-60`}
        >
          {isDownloading ? (
            <>
              <Loader className="h-4 w-4 animate-spin" />
              Downloading...
            </>
          ) : downloadStatus === 'completed' ? (
            <>
              <CheckCircle className="h-4 w-4" />
              Downloaded
            </>
          ) : (
            <>
              <Download className="h-4 w-4" />
              Download Test Cases
            </>
          )}
        </button>
      </div>
      {showApprovalWorkflow && !canProceedToPOM && (
        <p className="text-xs text-slate-500">
          {isApiScriptFlow ? 'API script generation' : 'Generate POM and export actions'} will be enabled after Lead approval. You can continue with other BRDs while this request is reviewed.
        </p>
      )}

      {!isApiScriptFlow && testingTypeMode !== 'web-only' && (
        <TestingTypeModal
          open={showTestingTypeModal}
          onClose={() => setShowTestingTypeModal(false)}
          onSelect={(type) => {
            setShowTestingTypeModal(false);
            if (onGeneratePOM) onGeneratePOM(type);
          }}
        />
      )}
    </div>
  );
};
